import type { CSSProperties } from 'react';
import { Cable, Cpu, FileText, Gamepad2, Joystick, Monitor, Video, Videotape, type LucideIcon } from 'lucide-react';
import { normalizeDeviceName } from '../lib/deviceNames';

const SYMBOL_ICONS = {
  cable: Cable,
  computer: Monitor,
  console: Gamepad2,
  fpga: Cpu,
  instructions: FileText,
  mvs: Joystick,
  video: Video,
  videotape: Videotape,
  crt: Monitor,
} satisfies Record<string, LucideIcon>;

export type DeviceIconSpec =
  | { kind: 'logo'; src: string }
  | { kind: 'symbol'; icon: keyof typeof SYMBOL_ICONS };

export const DEVICE_ICON_SPECS: Readonly<Record<string, DeviceIconSpec>> = {
  'Analogue Duo': { kind: 'logo', src: '/device-icons/analogue.svg' },
  'Analogue Mega Sg': { kind: 'logo', src: '/device-icons/analogue.svg' },
  'Analogue Nt mini Noir': { kind: 'logo', src: '/device-icons/analogue.svg' },
  'Analogue Pocket': { kind: 'logo', src: '/device-icons/analogue.svg' },
  'Analogue Super Nt': { kind: 'logo', src: '/device-icons/analogue.svg' },
  Arcade: { kind: 'logo', src: '/device-icons/arcade.svg' },
  'Arcade + NAC Spitfire': { kind: 'logo', src: '/device-icons/arcade.svg' },
  'Atari 2600': { kind: 'logo', src: '/device-icons/atari-2600.svg' },
  'Atari 7800': { kind: 'logo', src: '/device-icons/atari-7800.svg' },
  'Atari Jaguar': { kind: 'logo', src: '/device-icons/atari-jaguar.svg' },
  'Classic Mini Consoles': { kind: 'symbol', icon: 'console' },
  'Composite Video Only': { kind: 'symbol', icon: 'cable' },
  'Fujitsu FM Towns': { kind: 'logo', src: '/device-icons/fujitsu-fm-towns.svg' },
  HDMI: { kind: 'symbol', icon: 'cable' },
  LaserDisc: { kind: 'logo', src: '/device-icons/laserdisc.svg' },
  MiSTer: { kind: 'symbol', icon: 'fpga' },
  'Microsoft Xbox': { kind: 'logo', src: '/device-icons/xbox-original.svg' },
  'Microsoft PC': { kind: 'logo', src: '/device-icons/windows.svg' },
  'NEC PC-8801': { kind: 'symbol', icon: 'computer' },
  'NEC PC Engine SuperGrafx': { kind: 'logo', src: '/device-icons/nec-supergrafx.svg' },
  'Nintendo 3DS': { kind: 'logo', src: '/device-icons/nintendo-3ds.svg' },
  'Nintendo Game Boy Advance': { kind: 'logo', src: '/device-icons/nintendo-gba.svg' },
  'Nintendo GameCube': { kind: 'logo', src: '/device-icons/nintendo-gamecube.svg' },
  'Nintendo 64': { kind: 'logo', src: '/device-icons/nintendo-n64.svg' },
  'Nintendo Entertainment System / Family Computer': { kind: 'logo', src: '/device-icons/nintendo-nes.svg' },
  'Super Nintendo Entertainment System / Super Famicom': { kind: 'logo', src: '/device-icons/nintendo-snes.svg' },
  'Nintendo Switch': { kind: 'logo', src: '/device-icons/nintendo-switch.svg' },
  'Nintendo Wii': { kind: 'logo', src: '/device-icons/nintendo-wii.svg' },
  'Nintendo Wii U': { kind: 'logo', src: '/device-icons/nintendo-wiiu.svg' },
  'Panasonic 3DO Interactive Multiplayer': { kind: 'logo', src: '/device-icons/panasonic-3do.svg' },
  'Philips Compact Disc Interactive (CD-i)': { kind: 'logo', src: '/device-icons/philips-cdi.svg' },
  'RetroUSB AVS': { kind: 'symbol', icon: 'console' },
  'SNK Neo Geo Advanced Entertainment System': { kind: 'logo', src: '/device-icons/snk-neogeo.svg' },
  'SNK Neo Geo Multi Video System': { kind: 'symbol', icon: 'mvs' },
  'Sega Dreamcast': { kind: 'logo', src: '/device-icons/sega-dreamcast.svg' },
  'Sega Game Gear': { kind: 'logo', src: '/device-icons/sega-game-gear.svg' },
  'Sega Genesis & Mega Drive': { kind: 'logo', src: '/device-icons/sega-genesis.svg' },
  'Sega Master System': { kind: 'logo', src: '/device-icons/sega-master-system.svg' },
  'Sega Saturn': { kind: 'logo', src: '/device-icons/sega-saturn.svg' },
  'Sharp X68000': { kind: 'logo', src: '/device-icons/sharp-x68000.svg' },
  'Sony PlayStation': { kind: 'logo', src: '/device-icons/sony-ps1.svg' },
  'Sony PlayStation 2': { kind: 'logo', src: '/device-icons/sony-ps2.svg' },
  'Sony PlayStation 3': { kind: 'logo', src: '/device-icons/sony-ps3.svg' },
  'Sony PlayStation Portable & PlayStation Portable Go': { kind: 'logo', src: '/device-icons/sony-psp.svg' },
  'Sony PlayStation TV': { kind: 'logo', src: '/device-icons/sony-pstv.svg' },
  'VHS Capture': { kind: 'symbol', icon: 'videotape' },
  'Video Content': { kind: 'symbol', icon: 'video' },
  '_CRT Emulation': { kind: 'symbol', icon: 'crt' },
  '__Profile Instructions': { kind: 'symbol', icon: 'instructions' },
};

const UNKNOWN_DEVICE_ICON: DeviceIconSpec = { kind: 'symbol', icon: 'console' };

export function deviceIconSpec(device: string): DeviceIconSpec {
  return DEVICE_ICON_SPECS[normalizeDeviceName(device)] ?? UNKNOWN_DEVICE_ICON;
}

export function DeviceIcon({ device, compact = false }: { device: string; compact?: boolean }) {
  const spec = deviceIconSpec(device);
  const className = compact ? 'profile-device-icon compact-device-icon' : 'profile-device-icon';

  if (spec.kind === 'logo') {
    const style = { '--device-icon-url': `url("${spec.src}")` } as CSSProperties;
    return (
      <span
        aria-hidden="true"
        className={className}
        data-device={device}
        data-icon-kind="logo"
        data-testid={compact ? undefined : 'profile-device-icon'}
      >
        <span className="profile-device-logo" style={style} />
      </span>
    );
  }

  const SymbolIcon = SYMBOL_ICONS[spec.icon];
  return (
    <span
      aria-hidden="true"
      className={className}
      data-device={device}
      data-icon-kind="symbol"
      data-testid={compact ? undefined : 'profile-device-icon'}
    >
      <SymbolIcon className="profile-device-symbol" />
    </span>
  );
}
